<template>
  <div>
    <v-toolbar color="page-toolbar">
      <v-toolbar-title class="text-body-large">À propos de nous</v-toolbar-title>
    </v-toolbar>
    <v-container class="py-6 about-content">
      <!-- Brand header (docs/BRAND.md) -->
      <header class="about-header mb-6">
        <img src="@/assets/brand/logo.svg" alt="" width="72" height="72" class="about-header__logo" />
        <div>
          <h1 class="about-header__title">À propos de Québec <span class="about-header__accent">Eau Vive</span></h1>
          <p class="about-header__lead">
            Québec Eau Vive, anciennement Quebec Whitewater, est un projet bénévole fait par et pour les pagayeurs des
            rivières sportives du Québec.
          </p>
        </div>
      </header>

      <v-card variant="outlined" class="about-section">
        <h2><v-icon color="primary" class="mr-2">mdi-target</v-icon>Quels sont nos objectifs ?</h2>
        <p>
          Ce site s'adresse à tous les pagayeurs qui souhaitent pratiquer sur les rivières sportives du Québec. Notre
          mission est de rendre les données de débit d'eau accessibles au plus grand nombre, dans une base de données
          centralisée et facile à consulter.
        </p>
        <p>
          À partir de ces données, nous proposons des recommandations de débits minimum et maximum pour la pratique
          des sports d'eau vive, notamment le kayak.
        </p>
        <p>Nous mettons également à votre disposition :</p>
        <ul class="about-list">
          <li>Des informations pratiques pour vous aider à planifier vos sorties et naviguer plus sécuritairement.</li>
          <li>Un classement des rivières par région, pour faciliter la préparation de vos déplacements.</li>
          <li>Un contenu enrichi (photos et vidéos) pour mieux visualiser les sections.</li>
          <li>L'implantation progressive de caméras sur les rivières les plus complexes à interpréter.</li>
          <li>Une optimisation continue des calculs pour produire des estimations toujours plus fiables.</li>
        </ul>
      </v-card>

      <v-card variant="outlined" class="about-section">
        <h2><v-icon color="primary" class="mr-2">mdi-waves</v-icon>Notre approche de l'info-débit</h2>
        <p>
          Pour en savoir plus sur ce qu'est l'info-débit et comment nous calculons les niveaux, consultez notre
          <router-link :to="{ name: 'info-debit' }">page dédiée à l'info débit</router-link>.
        </p>
        <p>
          Les données brutes proviennent principalement du Centre d'Expertise Hydrique du Québec (CEHQ), organisme
          gouvernemental relevant du ministère de l'Environnement, ainsi que de données partagées par Hydro-Québec.
        </p>
        <p>
          Au Québec, un nombre limité de rivières disposent d'une station hydrométrique permettant de mesurer
          directement le débit (m3/s). Plusieurs rivières et affluents n'ont donc pas de mesure directe. L'un de nos
          objectifs principaux est de développer et d'appliquer les meilleures méthodes d'extrapolation possibles pour
          ces sections.
        </p>
      </v-card>

      <v-row dense class="about-row">
        <v-col cols="12" md="6">
          <v-card variant="outlined" class="about-section h-100">
            <h2>
              <v-icon color="primary" class="mr-2">mdi-chart-timeline-variant</v-icon>Ce qui distingue notre modèle
            </h2>
            <p>
              Les approches conventionnelles ne tiennent pas toujours compte de facteurs essentiels. Notre modèle
              intègre notamment :
            </p>
            <ul class="about-list">
              <li>La tendance actuelle du débit (montant, descendant ou stable).</li>
              <li>Les données de plusieurs stations voisines d'un même bassin hydrographique.</li>
              <li>L'analyse historique des stations hydrométriques disponibles.</li>
            </ul>
            <p>
              Notre outil permet également aux administrateurs d'ajuster certains paramètres de calcul pour chaque
              rivière, afin d'améliorer la précision des résultats selon la réalité du terrain.
            </p>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card variant="outlined" class="about-section h-100">
            <h2>
              <v-icon color="primary" class="mr-2">mdi-compare-horizontal</v-icon>Pourquoi nos débits peuvent différer
              d'autres sites
            </h2>
            <p>
              Il est normal que les débits affichés sur Québec Eau Vive diffèrent parfois d'autres plateformes. Nous
              utilisons une méthode distincte de l'approche classique. Une courte période d'adaptation peut donc être
              nécessaire, en particulier pour les sections de type creek, qui réagissent rapidement et parfois de
              façon imprévisible.
            </p>
            <p>
              Nous nous donnons un horizon de 5 ans pour consolider la validation des débits de la majorité des
              sections répertoriées. La contribution de la communauté est essentielle pour y parvenir.
            </p>
          </v-card>
        </v-col>
      </v-row>

      <v-card variant="outlined" class="about-section">
        <h2><v-icon color="primary" class="mr-2">mdi-file-chart-outline</v-icon>Publication des extrapolations</h2>
        <p>
          Notre approche ne repose pas sur une valeur d'extrapolation unique par section, mais sur un ensemble de
          paramètres dynamiques. Pris isolément, ces paramètres sont peu interprétables sans le contexte complet du
          modèle.
        </p>
        <p>
          L'analyse initiale requiert généralement de 2 à 4 heures par section, sans compter les ajustements et la
          validation sur le terrain.
        </p>
      </v-card>

      <!-- The page's one gold call-to-action (docs/BRAND.md) -->
      <v-card variant="flat" class="about-section about-help">
        <h2>
          <v-icon class="mr-2 about-help__icon">mdi-account-group</v-icon>Comment nous aider à améliorer la précision
        </h2>
        <p>
          Nous sommes une équipe de bénévoles. Il nous est impossible de valider toutes les rivières sans l'appui de la
          communauté. Si vous constatez un niveau inexact, merci de nous transmettre vos observations
          <a href="https://www.facebook.com/quebecwhitewater" target="_blank" rel="noopener noreferrer"
            >via notre page Facebook</a
          >.
        </p>
        <p>Après chaque sortie, vous pouvez nous partager :</p>
        <ul class="about-list">
          <li>Le nom de la rivière et de la section.</li>
          <li>La date de la sortie.</li>
          <li>La lecture de l'échelle locale (si disponible).</li>
          <li>Le niveau affiché sur Québec Eau Vive.</li>
          <li>L'appréciation du niveau (trop bas, minimum, parfait, maximum, trop haut).</li>
          <li>Votre estimation du débit en m3/s.</li>
          <li>La tendance du débit (montant, descendant ou stable).</li>
        </ul>
        <v-btn
          href="https://m.me/quebecwhitewater"
          target="_blank"
          rel="noopener noreferrer"
          color="gold"
          variant="flat"
          prepend-icon="mdi-facebook-messenger"
          class="mt-2"
        >
          Nous écrire sur Messenger
        </v-btn>
      </v-card>

      <p class="about-thanks">
        Merci à toutes les personnes qui contribuent déjà. Votre participation, même ponctuelle, aide directement toute
        la communauté et nous permet d'offrir un outil plus fiable et plus sécuritaire.
      </p>
    </v-container>
  </div>
</template>

<script>
  import { applySeoMeta, buildRouteSeo, signalPrerenderReady } from "@/utils/seo";

  export default {
    mounted() {
      this.$nextTick(() => {
        applySeoMeta({
          ...buildRouteSeo(this.$route),
          structuredData: {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "Notre approche de l'info-débit | À propos — Québec Eau Vive",
            description:
              "Découvrez comment Québec Eau Vive calcule l'info-débit des rivières du Québec : sources de données, méthode d'extrapolation et recommandations de débit pour les pagayeurs.",
            url: "https://quebecwhitewater.com/a-propos",
            inLanguage: "fr-CA",
          },
        });
        signalPrerenderReady();
      });
    },
  };
</script>

<style lang="scss" scoped>
  .about-content {
    max-width: 980px;
    line-height: 1.6;
  }

  .about-header {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .about-header__logo {
    flex: none;
  }

  .about-header__title {
    margin: 0 0 4px;
    font-size: 1.9rem;
    font-weight: 800;
    letter-spacing: -0.4px;
    line-height: 1.15;
    color: var(--qww-hero-wordmark);
  }

  .about-header__accent {
    font-weight: 400;
    color: var(--qww-hero-kicker);
  }

  .about-header__lead {
    margin: 0;
    font-size: 1.05rem;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }

  .about-section {
    padding: 20px 24px 12px;
    margin-bottom: 16px;
  }

  .about-row {
    margin-bottom: 4px;
  }

  .about-row .about-section {
    margin-bottom: 0;
  }

  h2 {
    display: flex;
    align-items: center;
    margin: 0 0 0.75rem;
    font-size: 1.25rem;
    line-height: 1.3;
  }

  p {
    margin-bottom: 0.8rem;
  }

  .about-list {
    padding-left: 1.4rem;
    margin-bottom: 0.9rem;
  }

  .about-list li {
    margin-bottom: 0.35rem;
  }

  .about-list li::marker {
    color: rgb(var(--v-theme-primary));
  }

  /* Pale gold card with the page's gold call-to-action */
  .about-help {
    padding-bottom: 20px;
    background: rgb(var(--v-theme-gold-tint));
    border: 1px solid rgba(var(--v-theme-gold), 0.5);
  }

  .about-help__icon {
    color: rgb(var(--v-theme-gold-text));
  }

  .about-thanks {
    margin-top: 8px;
    font-style: italic;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }

  @media (max-width: 600px) {
    .about-header {
      align-items: flex-start;
      gap: 14px;
    }

    .about-header__logo {
      width: 52px;
      height: 52px;
    }

    .about-header__title {
      font-size: 1.45rem;
    }

    .about-section {
      padding: 16px 16px 8px;
    }
  }
</style>
